import React, { useState } from "react";
import { Button } from "../../../shadcn/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../shadcn/components/ui/popover";
import { Calendar } from "../../../shadcn/components/ui/calendar";
import CalendarDotsIcon from "../../../icons/CalendarDotsIcon";
import ChevronUpSvg from "../../../icons/ChevronUpSvg";
import ChevronDownSvg from "../../../icons/ChevronDownSvg";
import StatusDropdown from "./StatusDropDown";

interface EventsFilterLeftProps {
  startDate: Date | undefined;
  endDate: Date | undefined;
  selectedStatus: string;
  statuses: string[];
  onStartDateChange: (date: Date | undefined) => void;
  onEndDateChange: (date: Date | undefined) => void;
  onStatusChange: (status: string) => void;
}

const EventsFilterLeft = React.memo(function EventsFilterLeft({
  startDate,
  endDate,
  selectedStatus,
  statuses,
  onStartDateChange,
  onEndDateChange,
  onStatusChange,
}: EventsFilterLeftProps) {
  const [isStartDateOpen, setIsStartDateOpen] = useState(false);
  const [isEndDateOpen, setIsEndDateOpen] = useState(false);

  const formatDate = (date: Date | undefined) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleStartDateSelect = (date: Date | undefined) => {
    onStartDateChange(date);
    setIsStartDateOpen(false);
  };

  const handleEndDateSelect = (date: Date | undefined) => {
    onEndDateChange(date);
    setIsEndDateOpen(false);
  };

  return (
    <div className="flex gap-2 lg:gap-4">
      <StatusDropdown
        statuses={statuses}
        selectedStatus={selectedStatus}
        onSelect={onStatusChange}
      />

      <Popover onOpenChange={setIsStartDateOpen} open={isStartDateOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="flex px-2 sm:px-4 gap-1 sm:gap-2 items-center w-full justify-between text-sm rounded-[8px] font-openSans text-brand-textLightGrey"
          >
            <CalendarDotsIcon className="w-3 h-3 sm:w-4 sm:h-4" />
            {startDate ? (
              <span className="font-openSans font-normal text-mobile-body-xsm leading-3 md:text-web-body-md md:leading-4">
                {formatDate(startDate)}
              </span>
            ) : (
              <span className="font-openSans font-normal text-mobile-body-xsm leading-3 md:text-web-body-md md:leading-4">
                Start date
              </span>
            )}
            <div className="px-1 lg:px-2">
              {isStartDateOpen ? (
                <ChevronUpSvg width={14} height={14} fill={"#7D7D7D"} />
              ) : (
                <ChevronDownSvg width={14} height={14} fill={"#7D7D7D"} />
              )}
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="bg-white w-fit">
          <div className="flex justify-end">
            <p
              onClick={() => {
                onStartDateChange(undefined);
              }}
              className="text-mobile-body-sm hover:cursor-pointer"
            >
              Clear
            </p>
          </div>
          <div className="flex justify-center -translate-y-2">
            <p className="font-openSans text-base text-center font-semibold text-brand-textLightGrey ">
              Select start date
            </p>
          </div>
          <Calendar
            className="bg-white"
            mode="single"
            selected={startDate}
            onSelect={handleStartDateSelect}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      <Popover onOpenChange={setIsEndDateOpen} open={isEndDateOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="flex px-2 sm:px-4 gap-1 sm:gap-2 items-center w-full justify-between text-sm rounded-[8px] font-openSans text-brand-textLightGrey"
          >
            <CalendarDotsIcon className="w-3 h-3 sm:w-4 sm:h-4" />
            {endDate ? (
              <span className="font-openSans font-normal text-mobile-body-xsm leading-3 md:text-web-body-md md:leading-4">
                {formatDate(endDate)}
              </span>
            ) : (
              <span className="font-openSans font-normal text-mobile-body-xsm leading-3 md:text-web-body-md md:leading-4">
                End date
              </span>
            )}
            <div className="px-1 lg:px-2">
              {isEndDateOpen ? (
                <ChevronUpSvg width={14} height={14} fill={"#7D7D7D"} />
              ) : (
                <ChevronDownSvg width={14} height={14} fill={"#7D7D7D"} />
              )}
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="pr-4 mr-4 bg-white w-fit">
          <div className="flex justify-end">
            <p
              onClick={() => {
                onEndDateChange(undefined);
              }}
              className="text-mobile-body-sm hover:cursor-pointer"
            >
              Clear
            </p>
          </div>
          <div className="flex justify-center -translate-y-2">
            <p className="font-openSans text-base text-center font-semibold text-brand-textLightGrey ">
              Select End date
            </p>
          </div>
          <Calendar
            mode="single"
            selected={endDate}
            onSelect={handleEndDateSelect}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
});

export default EventsFilterLeft;
